import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { RootState } from '@/store';
import { updateAddressDetails, setAddressCompleted, setLoading } from '@/store/slices/profileSlice';

const INDIAN_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir', 'Ladakh'
];

const AddressForm: React.FC = () => {
    const dispatch = useDispatch();
    const { addressDetails, loading } = useSelector((state: RootState) => state.profile);
    const { toast } = useToast();
    const [isEditing, setIsEditing] = useState(false);

    const [localData, setLocalData] = useState(addressDetails);

    const handleInputChange = (field: string, value: string) => {
        const updatedData = { ...localData, [field]: value };
        setLocalData(updatedData);
        dispatch(updateAddressDetails({ [field]: value }));
    };

    const validateForm = (): boolean => {
        const required = ['address', 'locality', 'city', 'state', 'pincode'];
        const missing = required.filter(field => !localData[field as keyof typeof localData]);

        if (missing.length > 0) {
            toast({
                title: 'Error',
                description: `Please fill all required fields: ${missing.join(', ')}`,
                variant: 'destructive'
            });
            return false;
        }

        if (!/^\d{6}$/.test(localData.pincode)) {
            toast({ title: 'Error', description: 'Please enter a valid 6-digit pincode', variant: 'destructive' });
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        dispatch(setLoading(true));
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            dispatch(setAddressCompleted(true));
            toast({ title: 'Success', description: 'Address details saved successfully!' });
        } catch (err) {
            toast({ title: 'Error', description: 'Failed to save address details', variant: 'destructive' });
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                    disabled={!isEditing}
                    id="address"
                    value={localData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Enter your full address"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="locality">Locality *</Label>
                    <Input
                        disabled={!isEditing}

                        id="locality"
                        value={localData.locality}
                        onChange={(e) => handleInputChange('locality', e.target.value)}
                        placeholder="Enter locality"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                        disabled={!isEditing}

                        id="city"
                        value={localData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="Enter city"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Select value={localData.state} onValueChange={(value) => handleInputChange('state', value)}
                        disabled={!isEditing}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                            {INDIAN_STATES.map((state) => (
                                <SelectItem key={state} value={state}>
                                    {state}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                        disabled={!isEditing}
                        id="pincode"
                        type="number"
                        value={localData.pincode}
                        onChange={(e) => handleInputChange('pincode', e.target.value)}
                        placeholder="Enter 6-digit pincode"
                        maxLength={6}
                    />
                </div>
            </div>

            <div className="flex gap-4 pt-6">

                {!isEditing ? (
                    <Button
                        onClick={() => setIsEditing(true)}
                        // className="flex-1 h-11 text-white button-effect font-medium"

                        className="flex-1 h-11 bg-flup-brand hover:bg-flup-brand-press text-white text-white font-medium w-full"
                    >
                        Edit Profile
                    </Button>
                ) : (


                    <>
                        <Button
                            onClick={handleSubmit}
                            disabled={loading}

                            className="flex-1 h-11 bg-flup-brand hover:bg-flup-brand-press text-white text-white font-medium"
                        >

                            {loading ? 'Saving...' : 'Save Address Details'}

                        </Button>
                        <Button
                            onClick={handleCancel}
                            variant="none"
                            className="flex-1 h-11 border-2 border-flup-hairline-2 text-flup-ink-2 font-medium hover:bg-flup-surface-alt"
                        >
                            Cancel
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default AddressForm;